import { useEffect } from 'react';
import { toast } from 'sonner';
import { useRealtimeNotificationsStore, type NotificationKind } from '@/stores/useRealtimeNotificationsStore';

interface MockEvent {
  kind: NotificationKind;
  title: string;
  description: string;
  link?: string;
}

const MOCK_EVENTS: MockEvent[] = [
  { kind: 'error', title: 'NC crítica detectada', description: 'WO-2026-0042 · defecto de tolerancia en ST-A3', link: '/quality/board' },
  { kind: 'warning', title: 'OEE bajo objetivo', description: 'LINE-A al 78% (objetivo 85%) en el turno actual', link: '/analytics' },
  { kind: 'info', title: 'Drift score actualizado', description: 'Modelo defect-yolo11 · PSI=0.18 (estable)', link: '/mlops/drift' },
  { kind: 'success', title: 'WO completada', description: 'WO-2026-0049 cerrada · 30/30 piezas OK', link: '/production/wip' },
  { kind: 'warning', title: 'Backorder material', description: 'EVA encapsulante · lead time 18 días', link: '/materials/inventory' },
  { kind: 'error', title: 'Parada no planificada', description: 'ROB-B1 sin comunicación (Profinet)', link: '/maintenance/condition' },
  { kind: 'info', title: 'Nuevo SSCC generado', description: 'Palet 00370123450000000031 · LINE-A', link: '/traceability/search' },
  { kind: 'success', title: 'Calibración OK', description: 'Cámara CAM-A2 dentro de tolerancia', link: '/quality/calibration' },
  { kind: 'warning', title: 'Mantenimiento próximo', description: 'CNC-A2 · RUL 72h (sustituir broca)', link: '/maintenance/predictive' },
  { kind: 'info', title: 'Sync ERP completado', description: '12 órdenes importadas desde Odoo', link: '/backoffice/erp-sync' },
];

const SIM_INTERVAL_MS = 30_000;

function fireToast(ev: MockEvent) {
  const opts = { description: ev.description };
  switch (ev.kind) {
    case 'success': toast.success(ev.title, opts); break;
    case 'warning': toast.warning(ev.title, opts); break;
    case 'error':   toast.error(ev.title, opts); break;
    default:        toast.info(ev.title, opts); break;
  }
}

/**
 * Real-time notifications simulator.
 *
 * Every {@link SIM_INTERVAL_MS}ms emits a random mock event:
 *  - Pushes it into {@link useRealtimeNotificationsStore} so the
 *    TopHeader bell shows the unread badge / dropdown list.
 *  - Fires a Sonner toast with the matching kind (success/warning/error/info).
 *
 * Mount once at the app root. Returns nothing.
 */
export function useRealtimeNotifications() {
  const add = useRealtimeNotificationsStore((s) => s.add);

  useEffect(() => {
    const tick = () => {
      const ev = MOCK_EVENTS[Math.floor(Math.random() * MOCK_EVENTS.length)];
      add({ kind: ev.kind, title: ev.title, description: ev.description, link: ev.link });
      fireToast(ev);
    };
    const id = setInterval(tick, SIM_INTERVAL_MS);
    return () => clearInterval(id);
  }, [add]);
}
